import React, { useEffect } from "react";
import useFindBuilderStore from "../store/useFindBuilderStore.js";
import { useNavigate } from "react-router-dom";
import { MapPin, Phone, ChevronRight, Compass, UserCheck } from 'lucide-react'; // Added UserCheck

// --- Placeholder Image Path ---
const PLACEHOLDER_IMAGE = "https://placehold.co/100x100/eee/ccc?text=Logo"; // Smaller, square placeholder

const BuildersList = () => {
  const {
    states,
    selectedState,
    setSelectedState,
    setSelectedDistrict,
    setSelectedTaluk,
    fetchLocationData,
    builders,
    isLoading,
    selectedDistrict,
    selectedTaluk,
    fetchBuilders,
    fetchMappedDistricts,
    fetchMappedTaluks,
    mappedDistricts,
    mappedTaluks,
  } = useFindBuilderStore();

  useEffect(() => {
    fetchLocationData();
    fetchMappedDistricts();
  }, [fetchLocationData, fetchMappedDistricts]);

  useEffect(() => {
    if (selectedDistrict) {
      fetchMappedTaluks(selectedDistrict);
    } else {
       useFindBuilderStore.setState({ mappedTaluks: [], selectedTaluk: "" });
    }
  }, [selectedDistrict, fetchMappedTaluks]);

  useEffect(() => {
    if (selectedDistrict) {
      fetchBuilders();
    } else {
      useFindBuilderStore.setState({ builders: [] });
    }
  }, [selectedDistrict, selectedTaluk, fetchBuilders]);


  const navigate = useNavigate();

  // Shortened address format
  const formatShortAddress = (builder) => {
    const parts = [
      builder.taluk, // Prioritize Taluk for locality
      builder.district,
    ].filter(Boolean);
    return parts.join(', ') || "Location Unavailable";
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <div className="w-full py-6 px-4 md:px-6">
        {/* Note */}
        <div className="text-center mb-5 text-gray-600 font-medium text-xs md:text-sm">
          Join us with Ducktail once you choose your Builder to get support access from Ducktail.
        </div>

        {/* Filter Section - Remains the same */}
        <div className="flex justify-center mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl w-full">
            {/* State Dropdown */}
          {/* <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">
              State
            </label>
            <div className="relative bg-white p-0.5 rounded-md shadow-sm border border-gray-200 h-8 w-full flex items-center justify-center">
              <span className="text-xs text-gray-700">Tamil Nadu</span>
            </div>
          </div> */}
            {/* District Dropdown */}
            <div className="flex flex-col w-full md:w-[300px]">
              <label className="text-xs font-medium text-gray-500 mb-1">
                District
              </label>
              <div
                className={`relative bg-white p-0.5 rounded-md shadow-sm border ${
                  !selectedState ? "border-gray-200 bg-gray-100 opacity-70" : "border-gray-200"
                } h-8`}
              >
                <select
                  className={`w-full h-full bg-transparent border-none rounded-md focus:ring-1 focus:ring-blue-300 focus:outline-none cursor-pointer appearance-none text-xs px-2 pr-6 ${
                    !selectedState ? "text-gray-400" : "text-gray-700"
                  }`}
                  value={selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                  disabled={!selectedState || !mappedDistricts || mappedDistricts.length === 0}
                >
                  <option value="" className="py-1 text-gray-400">Select District</option>
                  {mappedDistricts.districts?.map((district) => (
                    <option key={district} value={district} className="text-gray-800">
                      {district}
                    </option>
                  ))}
                </select>
                <ChevronRight className="absolute top-1/2 right-2 transform -translate-y-1/2 pointer-events-none w-3 h-3 text-gray-400 rotate-90" />
              </div>
            </div>

            {/* Taluk Dropdown */}
            <div className="flex flex-col w-full md:w-[300px]">
              <label className="text-xs font-medium text-gray-500 mb-1">
                Taluk
              </label>
              <div
                className={`relative bg-white p-0.5 rounded-md shadow-sm border ${
                  !selectedDistrict ? "border-gray-200 bg-gray-100 opacity-70" : "border-gray-200"
                } h-8`}
              >
                <select
                   className={`w-full h-full bg-transparent border-none rounded-md focus:ring-1 focus:ring-blue-300 focus:outline-none cursor-pointer appearance-none text-xs px-2 pr-6 ${
                    !selectedDistrict ? "text-gray-400" : "text-gray-700"
                  }`}
                  value={selectedTaluk}
                  onChange={(e) => setSelectedTaluk(e.target.value)}
                  disabled={!selectedDistrict || !mappedTaluks || mappedTaluks.length === 0}
                >
                  <option value="" className="py-1 text-gray-400">Select Taluk</option>
                  {mappedTaluks.taluks?.map((taluk) => (
                    <option key={taluk} value={taluk} className="text-gray-800">
                      {taluk}
                    </option>
                  ))}
                </select>
                <ChevronRight className="absolute top-1/2 right-2 transform -translate-y-1/2 pointer-events-none w-3 h-3 text-gray-400 rotate-90" />
              </div>
            </div>
          </div>
        </div>

        {/* Builder Cards Grid - Adjusted grid for potentially more cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4 max-w-7xl mx-auto"> {/* Added xl:grid-cols-5 and centering */}
          {isLoading ? (
             <div className="col-span-full flex justify-center items-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
             </div>
          ) : builders && builders.length > 0 ? (
            builders.map((builder) => (
              <div
                key={builder.builder_id}
                className="bg-white rounded-lg overflow-hidden shadow-md border border-gray-200 flex flex-col transition-shadow duration-300 hover:shadow-lg" // Slightly increased shadow on hover
              >
                {/* Card Body - Row Layout */}
                <div className="p-3 flex items-start gap-3"> {/* Use flex row, smaller padding */}
                   {/* Image Left */}
                   <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0 border border-gray-100"> {/* Smaller, rounded image */}
                    <img
                      src={builder.profile_image || PLACEHOLDER_IMAGE}
                      alt={`${builder.company_name} Logo`}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.onerror = null; e.target.src=PLACEHOLDER_IMAGE }}
                    />
                  </div>

                  {/* Info Block Right */}
                  <div className="flex-grow min-w-0"> {/* Allow shrinking */}
                    {/* Optional: New Launch Tag */}
                    {/* <span className="text-[9px] font-bold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded-sm mb-1 inline-block">NEW LAUNCH</span> */}

                    <h3 className="font-semibold text-sm text-gray-800 truncate mb-0.5">
                      {builder.company_name || "Company Unavailable"}
                    </h3>
                    <p className="text-xs text-gray-500 truncate mb-1">
                      {/* Using short address instead of engineer name */}
                      {formatShortAddress(builder)}
                    </p>

                    {/* Row for Verified/ID */}
                    <div className="flex items-center gap-2 mb-1.5">
                      {builder.is_verified && (
                          <span className="inline-flex items-center bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full text-[9px] font-medium"> {/* Even smaller */}
                            <UserCheck className="w-2 h-2 mr-0.5" /> {/* Tiny icon */}
                            Verified
                          </span>
                       )}
                      <span className="text-[9px] text-gray-400"> {/* Tiny text */}
                        ID: {builder.ducktail_id || "N/A"}
                      </span>
                    </div>

                     {/* Row for Phone */}
                    <div className="flex items-center text-[10px] text-gray-500">
                       <Phone className="w-2.5 h-2.5 mr-1 text-gray-400" />
                       <span>{builder.contact_number || "N/A"}</span>
                    </div>
                  </div>
                </div>

                 {/* Bottom Action Bar */}
                 <div className="border-t border-gray-100 mt-auto px-3 py-2 flex justify-between items-center"> {/* Separator, padding */}
                    <button className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-1 transition-colors duration-200">
                        <Compass className="w-3 h-3"/>
                        Get Direction
                    </button>
                    <button
                      className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs font-semibold transition-colors duration-200" // Adjusted styling
                      onClick={() => navigate(`/buildersinfo/${builder.builder_id}`)}
                    >
                      VIEW MORE
                    </button>
                 </div>
              </div>
            ))
          ) : (
             <div className="col-span-full text-center py-10 text-gray-500 text-sm">
               No builders found for the selected location. Please select a district.
             </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuildersList;